import { useMemo, useState } from "react"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setModalData } from "@/store/globalSlice"
import { TrophyIcon } from "@heroicons/react/24/solid"
import PlayerListItemInfo from "@/Pages/Game/PlayerListItemInfo"
import classNames from "classnames"
import Money from "@/Components/Money"
import { useNavigate } from "react-router-dom"
import { getUserCreds, socket } from "@/utils/utils"
import { GameEndModalCTA } from "@/Modals/GameEndModalCTA"
import Modal from "@/Components/Modal"
import GameStatsTable from "@/Components/GameStatsTable"
import NetWorthBar from "@/Components/NetWorthBar"
import EndGameBreakdownModal from "@/Modals/EndGameBreakdownModal"
import {
  WORTH_SEGMENTS,
  buildNetWorthBreakdown,
  type EndGamePlayerWorth,
} from "@/utils/endGameBreakdown"

// The little sub-label under each bucket's amount, so the number says what it
// is made of without needing the detailed view.
const segmentCaption = (
  key: (typeof WORTH_SEGMENTS)[number]["key"],
  worth: EndGamePlayerWorth
) => {
  switch (key) {
    case "cash":
      return "in hand"
    case "stockValue":
      return `${worth.stockShares.toLocaleString()} share${
        worth.stockShares === 1 ? "" : "s"
      }`
    case "propertyValue":
      return `${worth.propertyCount} owned`
    case "houseValue": {
      if (!worth.houseCount && !worth.hotelCount) return "none built"
      const parts = []
      if (worth.houseCount)
        parts.push(`${worth.houseCount} house${worth.houseCount === 1 ? "" : "s"}`)
      if (worth.hotelCount)
        parts.push(`${worth.hotelCount} hotel${worth.hotelCount === 1 ? "" : "s"}`)
      return parts.join(", ")
    }
  }
}

const EndGameModal: React.FC = () => {
  const dispatch = useAppDispatch()
  const myGame = useAppSelector((state) => state.global.myGame)
  const navigate = useNavigate()
  const [breakdownVisible, setBreakdownVisible] = useState(false)

  // Richest first, and the same balance sheet the detailed view reads.
  const sheets = useMemo(() => buildNetWorthBreakdown(myGame), [myGame])
  const winnerOrder = sheets.length ? sheets[0].order : null

  const handleClose = () => {
    setBreakdownVisible(false)
    dispatch(setModalData({ endGame: { visible: false } }))
  }

  const handleGoToLobby = () => {
    handleClose()
    const thisPlayer = myGame?.situation?.players.find(
      (item) => item.id === getUserCreds().email
    )
    socket.emit("game.leave-game", myGame?.id || "", thisPlayer!.order)
    navigate("/lobby")
  }

  const modalData = useAppSelector((state) => state.global.modalData)

  if (!modalData.endGame?.visible) return null

  // Players in standings order, for the recap table and the detailed view.
  const rankedPlayers = sheets
    .map((sheet) =>
      myGame?.situation?.players.find((player) => player.order === sheet.order)
    )
    .filter((player): player is IPlayer => !!player)

  const wideCards = sheets.length <= 4

  // This screen is meant to be read without scrolling: the net-worth cards and
  // the game-stats recap both fit, and the itemised, per-property detail lives
  // behind "Full Breakdown" instead. Only the card grid can scroll, and only in
  // the rare case it can't fit, so neither the recap nor the buttons are ever
  // pushed off screen. The pt leaves room for the winner card's trophy badge
  // (-top-2 plus its ring), which the scroll container would otherwise clip.
  return (
    <>
      <Modal
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-[86rem] max-h-[92vh] z-[100] flex flex-col"
        onClose={handleClose}
        icon={<TrophyIcon className="h-5 w-5 text-white" />}
        title="Game Over!"
        subtitle="Final standings by net worth"
        headerSize="sm"
        contentClassName="flex-1 min-h-0 overflow-hidden px-6 pt-1 pb-5 flex flex-col"
      >
        <div
          className={classNames(
            "grid min-h-0 flex-1 content-start gap-3 overflow-y-auto px-1 pt-2",
            {
              "grid-cols-3": !wideCards,
              "grid-cols-2": wideCards,
            }
          )}
        >
          {sheets.map((worth) => {
            const user = myGame?.players.find((p) => p.email === worth.playerId)
            const gamePlayer = myGame?.situation?.players.find(
              (p) => p.order === worth.order
            )
            if (!user || !gamePlayer) return null

            const isWinner = worth.order === winnerOrder

            return (
              <div
                key={worth.order}
                className={classNames(
                  "relative p-3 rounded-2xl transition-all duration-300 h-fit border",
                  {
                    "bg-gradient-to-br from-yellow-300/10 via-amber-200/5 to-transparent border-yellow-500/30 ring-1 ring-yellow-300/30 shadow-[0_0_28px_-6px_rgba(234,179,8,0.3)]":
                      isWinner,
                    "bg-white/5 border-gray-700/60": !isWinner,
                  }
                )}
              >
                {isWinner && (
                  <div className="absolute -top-2 -right-2 bg-gradient-to-br from-yellow-400 to-amber-600 rounded-full p-1.5 shadow-lg shadow-amber-500/30 ring-2 ring-gray-950">
                    <TrophyIcon className="text-white h-5 w-5" />
                  </div>
                )}

                <div className="flex justify-between items-center gap-3">
                  <PlayerListItemInfo
                    player={gamePlayer}
                    user={user}
                    distinguishActivePlayer={false}
                  />

                  <div className="flex flex-col items-end gap-0.5">
                    <div
                      className={classNames(
                        "text-xs font-poppins font-semibold",
                        {
                          "text-yellow-200/90": isWinner,
                          "text-white/50": !isWinner,
                        }
                      )}
                    >
                      Net Worth
                    </div>
                    <Money
                      money={worth.totalWorth.toLocaleString()}
                      className={classNames(
                        "text-2xl font-bold font-poppins gap-2",
                        {
                          "text-yellow-300 drop-shadow-[0_2px_2px_rgba(0,0,0,0.5)]":
                            isWinner,
                          "text-white": !isWinner,
                        }
                      )}
                      iconClass="h-6"
                    />
                  </div>
                </div>

                <NetWorthBar worth={worth} className="mt-2.5" />

                <div
                  className={classNames("mt-2 grid gap-1.5", {
                    "grid-cols-4": wideCards,
                    "grid-cols-2": !wideCards,
                  })}
                >
                  {WORTH_SEGMENTS.map((segment) => (
                    <div
                      key={segment.key}
                      className={classNames("rounded-xl border px-2.5 py-1.5", {
                        "bg-amber-400/10 border-amber-400/20": isWinner,
                        "bg-black/25 border-white/5": !isWinner,
                      })}
                    >
                      <div className="flex items-center gap-1.5 font-poppins text-[0.7rem] text-white/50">
                        <span
                          className="h-1.5 w-1.5 shrink-0 rounded-full"
                          style={{ backgroundColor: segment.color }}
                        />
                        {segment.label}
                      </div>
                      <Money
                        money={worth[segment.key].toLocaleString()}
                        className="text-base font-poppins font-semibold text-white gap-1"
                        iconClass="h-4"
                      />
                      <div className="font-poppins text-[0.65rem] text-white/40">
                        {segmentCaption(segment.key, worth)}
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            )
          })}
        </div>

        {/* Full-game recap, same table the bankruptcy-win screen shows. Rent
            earned here is the value each player's properties returned; the
            per-property split is behind "Full Breakdown". */}
        <div className="mt-4 shrink-0">
          <div className="mb-1.5 flex items-center gap-3">
            <h3 className="font-poppins text-xs font-semibold uppercase tracking-[0.25em] text-white/50">
              Game Stats
            </h3>
            <div className="h-px flex-1 bg-white/10" />
          </div>
          <GameStatsTable
            dense
            players={rankedPlayers}
            users={myGame?.players}
            winnerOrder={winnerOrder}
            historyItems={myGame?.situation?.historyItems}
            completedTrades={myGame?.situation?.completedTrades}
          />
        </div>

        <GameEndModalCTA
          onGoToLobby={handleGoToLobby}
          onShowBreakdown={() => setBreakdownVisible(true)}
        />
      </Modal>

      {breakdownVisible && (
        <EndGameBreakdownModal
          sheets={sheets}
          players={rankedPlayers}
          winnerOrder={winnerOrder}
          onClose={() => setBreakdownVisible(false)}
        />
      )}
    </>
  )
}

export default EndGameModal
